import {
  ApexChart,
  ApexXAxis,
  ApexYAxis,
  ApexPlotOptions,
  ApexDataLabels,
  ApexLegend,
  ApexTooltip,
  ApexTitleSubtitle,
  ApexGrid,
  ApexAxisChartSeries,
  ApexResponsive,
  ApexStates
} from 'ng-apexcharts';
import { UserTypes } from './user.model';

interface ApexRuntimeTooltip {
  getElTooltip?(): HTMLElement | null;
  handleMouseOut?(options: { tooltipEl: HTMLElement }): void;
}

interface ApexRuntimeContext {
  toggleDataPointSelection?(seriesIndex: number, dataPointIndex?: number): void;
  w?: {
    dom?: {
      baseEl?: HTMLElement;
    };
    globals?: {
      axisCharts?: boolean;
      tooltip?: ApexRuntimeTooltip;
    };
    interact?: {
      selectedDataPoints?: number[][];
    };
  };
}

export function clearApexChartHover(chartContext: ApexRuntimeContext | null | undefined): void {
  const baseElement = chartContext?.w?.dom?.baseEl;
  const tooltip = chartContext?.w?.globals?.tooltip;
  const tooltipElement = tooltip?.getElTooltip?.() ?? baseElement?.querySelector<HTMLElement>('.apexcharts-tooltip');

  if (tooltipElement && tooltip?.handleMouseOut) {
    tooltip.handleMouseOut({ tooltipEl: tooltipElement });
    return;
  }

  baseElement?.classList.remove('apexcharts-tooltip-active');
  tooltipElement?.classList.remove('apexcharts-active');
  baseElement
    ?.querySelectorAll(
      '.apexcharts-xcrosshairs, .apexcharts-ycrosshairs, .apexcharts-xaxistooltip, .apexcharts-yaxistooltip'
    )
    .forEach(element => element.classList.remove('apexcharts-active'));
}

export function clearApexChartSelection(chartContext: ApexRuntimeContext | null | undefined): void {
  const selectedDataPoints = chartContext?.w?.interact?.selectedDataPoints;

  if (!selectedDataPoints?.length || !chartContext?.toggleDataPointSelection) {
    return;
  }

  const selectedPoints = selectedDataPoints.flatMap((dataPointIndexes, seriesIndex) =>
    dataPointIndexes.map(dataPointIndex => ({ seriesIndex, dataPointIndex }))
  );
  const isAxisChart = chartContext.w?.globals?.axisCharts !== false;

  selectedPoints.forEach(({ seriesIndex, dataPointIndex }) => {
    if (isAxisChart) {
      chartContext.toggleDataPointSelection?.(seriesIndex, dataPointIndex);
      return;
    }

    chartContext.toggleDataPointSelection?.(dataPointIndex);
  });
}

export function clearApexChartInteraction(chartContext: ApexRuntimeContext | null | undefined): void {
  clearApexChartSelection(chartContext);
  clearApexChartHover(chartContext);
}

export const NO_PERSISTENT_CHART_SELECTION_EVENTS: NonNullable<ApexChart['events']> = {
  mouseLeave: (_event, chartContext) => {
    clearApexChartHover(chartContext);
  },
  dataPointSelection: (_event, chartContext, options) => {
    const selectedPoints = options?.selectedDataPoints?.[options.seriesIndex];

    if (!selectedPoints?.includes(options.dataPointIndex)) {
      return;
    }

    if (options?.w?.globals?.axisCharts === false) {
      chartContext?.toggleDataPointSelection(options.dataPointIndex);
      return;
    }

    chartContext?.toggleDataPointSelection(options.seriesIndex, options.dataPointIndex);
  }
};

export const NO_PERSISTENT_CHART_SELECTION_STATES: ApexStates = {
  active: {
    allowMultipleDataPointsSelection: false,
    filter: { type: 'none' }
  }
};

export interface IEarningsApiResponse {
  trend: 'up' | 'down' | 'neutral';
  trend_percentage: number;
  active_okeey_cash_accounts: string;
  comparison_vs_last_month: string;
  data: IMonthlyEarning[];
}

export interface IMonthlyEarning {
  month: string;
  year: number;
  earnings: number;
}

export interface IMember {
  user_type: UserTypes;
  direct: number;
  network: number;
}

export interface IMemberApiResponse {
  direct_referrals: number;
  members: IMember[];
  trend: string;
}

export interface BaseChartOptions {
  chart: ApexChart;
  xaxis?: ApexXAxis;
  yaxis?: ApexYAxis;
  plotOptions?: ApexPlotOptions;
  dataLabels?: ApexDataLabels;
  legend?: ApexLegend;
  tooltip?: ApexTooltip;
  grid?: ApexGrid;
  colors?: string[];
  states?: ApexStates;
  title?: ApexTitleSubtitle;
  subtitle?: ApexTitleSubtitle;
  series?: ApexAxisChartSeries;
  lines: any;
  responsive?: ApexResponsive[];
}

export type ChartOptions = BaseChartOptions & {
  series: ApexAxisChartSeries;
};

export const DEFAULT_CHART_OPTIONS: BaseChartOptions = {
  chart: {
    toolbar: { show: false },
    animations: { enabled: true },
    type: 'line',
    events: NO_PERSISTENT_CHART_SELECTION_EVENTS
  },
  plotOptions: {
    bar: {
      borderRadius: 4,
      columnWidth: '70%'
    }
  },
  dataLabels: { enabled: false },
  legend: {
    position: 'bottom',
    horizontalAlign: 'center',
    onItemClick: {
      toggleDataSeries: true
    }
  },
  tooltip: {
    enabled: false,
    shared: true,
    intersect: false
  },
  lines: {
    show: false
  },
  grid: {
    show: false
  },
  colors: ['#007DCB', '#8AC3E7'],
  states: {
    ...NO_PERSISTENT_CHART_SELECTION_STATES,
    hover: { filter: { type: 'none' } }
  },
  responsive: [
    {
      breakpoint: 600,
      options: {
        plotOptions: {
          bar: { barHeight: '30%' }
        }
      }
    }
  ]
};
